/**
 * How the analysis pane presents *not having a plot* — and the one place that
 * decides it.
 *
 * ## Why this is a module and not two ternaries
 *
 * There are two kinds of "no plot", and they are not the same event:
 *
 * - **`error`** — something went wrong. A read failed, a header did not parse.
 * - **`unsupported`** — a scheme we have chosen not to implement yet. Nothing
 *   failed, and the visitor did nothing wrong.
 *
 * The second wore the first's chrome: a red banner reading *"Error: IQ3_XXS
 * isn't a float type"*. Three visitors filed bug reports against that sentence
 * within hours of the report button shipping, and on the models it affects it is
 * not an edge case — 65%, 84% and 92% of the bytes in the three reported
 * checkpoints are stored in schemes we cannot decode, so for those visitors the
 * red banner *is* the product.
 *
 * The reviewer's ruling (2026-08-13) was to make it structural rather than
 * documented: *"a convention relies on memory; a type relies on the compiler"*.
 * So the kind is a type, and this function is the only thing that turns a kind
 * into presentation. Both consumers — the pane's markup and the live-region
 * announcement — read the same record.
 *
 * That matters most for the part nobody looks at. `tone: "error"` writes to the
 * **assertive** live region and prefixes "Error:"; `note` writes to the polite
 * one. A chosen limitation announced as an error is the same defect on the
 * screen-reader path as the red banner is on the visual one, and it is the one
 * you cannot see in a screenshot. Deriving both from one value is what stops
 * them drifting apart.
 *
 * Pure: a kind in, a record out. No DOM, no state.
 */

/** Why the analysis pane has no plot, and which kind of nothing it is. */
export interface AnalysisOutcome {
  readonly kind: "error" | "unsupported";
  readonly message: string;
}

export interface OutcomePresentation {
  /**
   * Which live region announces it, and whether it is prefixed "Error:".
   *
   * `note` is the polite region. An unsupported scheme is information, and
   * interrupting a screen-reader user to tell them we have not written a
   * decoder is the announcement equivalent of the red banner.
   */
  readonly tone: "error" | "note";
  /** The notice's modifier class — informational, not a warning. */
  readonly noticeClass: string;
  /**
   * The bolded lead.
   *
   * "Not decoded yet" rather than "Could not read that tensor": the first puts
   * the limitation with us, the second implies the file or the click was at
   * fault. We read that tensor fine — its shape, role and byte count are on
   * screen and exact.
   */
  readonly heading: string;
}

export function presentOutcome(kind: AnalysisOutcome["kind"]): OutcomePresentation {
  return kind === "unsupported"
    ? {
        tone: "note",
        noticeClass: "notice--info",
        heading: "Not decoded yet.",
      }
    : {
        tone: "error",
        noticeClass: "notice--warn",
        heading: "Could not read that tensor.",
      };
}
